import { Button, Card, CardContent, cn, Input } from '@solinkcorp/solink-ui';
import { MapPinOff } from 'lucide-react';
import React, { useId } from 'react';
import useLocale from 'src/hooks/useLocale';
import { type GeolocationFailureReason } from 'utils/GeolocationUtil';
import {
  CARD_MAX_WIDTH_CLASS,
  PRIMARY_BUTTON_CLASS,
  STEP_CARD_MIN_HEIGHT_CLASS,
} from '../constants';

interface LocationConfirmationProps {
  failureReason: GeolocationFailureReason | null;
  storeNumber: string;
  onStoreNumberChange: (value: string) => void;
  onSubmitStoreNumber: () => void;
  onRetryLocation: () => void;
}

// Fallback when GPS fails: type the store number from the QR sticker instead.
// GPS retry (onRetryLocation) is still wired through the hook but hidden here for now.
const LocationConfirmation: React.FC<LocationConfirmationProps> = ({
  failureReason,
  storeNumber,
  onStoreNumberChange,
  onSubmitStoreNumber,
}) => {
  const locale = useLocale();
  const storeNumberInputId = useId();

  return (
    <Card className={cn(CARD_MAX_WIDTH_CLASS, STEP_CARD_MIN_HEIGHT_CLASS)}>
      <CardContent className="flex flex-col items-center gap-4 text-center">
        <MapPinOff className="size-6 text-foreground-secondary" aria-hidden="true" />
        <p className="text-foreground-secondary body-md">
          {failureReason === 'denied'
            ? locale.virtualTablet.locationDeniedMessage
            : locale.virtualTablet.locationTimeoutMessage}
        </p>
        <div className="flex w-full flex-col gap-2 text-left">
          <label htmlFor={storeNumberInputId} className="text-text-primary label-sm">
            {locale.virtualTablet.storeNumberLabel}
          </label>
          <Input
            id={storeNumberInputId}
            // Aspen Input field: 8px padding, surface fill, 2px accent border on focus (Figma 6832:39798).
            className="bg-background-surface px-2 focus-visible:border-2 focus-visible:border-border-accent focus-visible:ring-0"
            type="text"
            inputMode="numeric"
            placeholder={locale.virtualTablet.storeNumberPlaceholder}
            value={storeNumber}
            onChange={(e) => onStoreNumberChange(e.target.value)}
            data-testid="store-number-input"
          />
        </div>
        <Button
          type="button"
          disabled={!storeNumber.trim()}
          className={cn('h-9', PRIMARY_BUTTON_CLASS)}
          onClick={onSubmitStoreNumber}
          data-testid="submit-store-number-button"
        >
          {locale.general.continue}
        </Button>
      </CardContent>
    </Card>
  );
};

export default LocationConfirmation;
